export interface ForecastProvider {
  key: string; name: string; model_id: string; revision: string; usage: string;
  max_context: number; max_horizon: number; max_tuning_horizon: number;
  quantiles: number[]; tuning_modes: string[]; tuning_implementation: string;
}

export interface ForecastSpec {
  kind: 'foundation_forecast'; schema_version: 1; provider: string;
  model_id?: string; revision?: string;
  context_length: number; prediction_length: number; quantiles: number[];
  data: {
    timestamp_column: string; series_columns: string[]; target_columns: string[];
    past_covariates: string[]; future_covariates: string[];
    frequency: string; calendar: string; missing_policy: string;
    available_at_column?: string | null;
    future_available_at_column?: string | null;
    seasonal_period?: number | null;
  };
}

export interface ForecastRow {
  series_id: string; target: string; forecast_origin: string; timestamp: string;
  horizon: number; prediction: number; actual?: number | null;
  quantiles: Record<string, number>; baseline: number;
}

export const defaultForecastSpec: ForecastSpec = {
  kind: 'foundation_forecast', schema_version: 1, provider: 'timesfm3',
  context_length: 512, prediction_length: 24, quantiles: [0.1, 0.5, 0.9],
  data: { timestamp_column: 'timestamp', series_columns: ['symbol'], target_columns: ['close'],
    past_covariates: [], future_covariates: [], frequency: '1h', calendar: 'continuous', missing_policy: 'mask' },
};

export function validateForecastForm(spec: ForecastSpec, provider: ForecastProvider | undefined, mode: string): string | null {
  if (!provider) return 'Choose a model.';
  if (!Number.isInteger(spec.context_length) || spec.context_length < 32 || spec.context_length > provider.max_context) return `Lookback must be 32–${provider.max_context} observations.`;
  const maxHorizon = mode === 'pretrained' ? provider.max_horizon : provider.max_tuning_horizon;
  if (!Number.isInteger(spec.prediction_length) || spec.prediction_length < 1 || spec.prediction_length > maxHorizon) return `Forecast horizon must be 1–${maxHorizon} observations for this mode.`;
  if (!spec.data.timestamp_column || !spec.data.target_columns.length) return 'Map the timestamp and target columns.';
  const columns = [spec.data.timestamp_column, ...spec.data.series_columns, ...spec.data.target_columns, ...spec.data.past_covariates, ...spec.data.future_covariates];
  if (new Set(columns).size !== columns.length) return 'Each column must have one role.';
  for (const field of ['available_at_column', 'future_available_at_column'] as const) {
    const column = spec.data[field];
    if (column != null && (!column || columns.includes(column))) return 'Availability timestamps must use separate columns from model inputs.';
  }
  return null;
}

export function selectForecastProvider(spec: ForecastSpec, provider: ForecastProvider): ForecastSpec {
  return { ...spec, provider: provider.key, model_id: provider.model_id, revision: provider.revision };
}

export function chartForecasts(rows: ForecastRow[], series: string, origin: string) {
  return rows.filter(row => `${row.series_id} / ${row.target}` === series && row.forecast_origin === origin)
    .sort((a, b) => a.horizon - b.horizon).map(row => {
      const values = Object.entries(row.quantiles).sort(([a], [b]) => Number(a) - Number(b));
      return { ...row, band: values.length > 1 ? [values[0][1], values[values.length - 1][1]] : undefined };
    });
}
